import WeatherWidgetDetails from 'features/Weather/components/WeatherWidgetDetails/WeatherWidgetDetails';
import ConditionIcons from 'features/Weather/components/ConditionIcons/ConditionIcons';
import { useId } from 'react';
import { formatDateTime } from 'utils/dates/date.utils';
import {
  formatCurrentConditionTemperature,
  parseCurrentConditionObservationTime,
} from '../../../../features/CurrentConditions/utils/currentConditions.utils';
import {
  currentConditionsDetailGroups,
  currentConditionsDetails,
} from '../../config/currentConditionsDetails.config';

const getStationName = (condition) => {
  const cityName = String(condition?.city_name || '')
    .trim()
    .replace(/\s{2,}/g, ' - ');
  const stationCode = String(condition?.airport_code ?? condition?.iata_code ?? '').trim();

  if (cityName) return cityName;
  if (stationCode) return `Station ${stationCode}`;
  return 'Reporting station';
};

const CurrentConditionsStationSummary = ({ condition, isNearestStation = false }) => {
  const headingId = useId();

  if (!condition) {
    return (
      <section
        aria-labelledby={headingId}
        className="current-conditions-station-summary flex-shrink-0"
      >
        <h2 id={headingId} className="mb-1 fs-6 fw-bold">
          Nearby conditions
        </h2>
        <p className="mb-0 text-secondary" role="status">
          A nearby reporting station could not be identified for your current location.
        </p>
      </section>
    );
  }

  const stationName = getStationName(condition);
  const observationTimestamp = parseCurrentConditionObservationTime(condition.utc_time);
  const observationTime =
    observationTimestamp === null ? null : formatDateTime(new Date(observationTimestamp));
  const formattedTemperature = formatCurrentConditionTemperature(condition.temperature);
  const temperatureLabel =
    formattedTemperature === null ? 'Unavailable' : `${formattedTemperature}°F`;
  const skyCondition = String(condition.sky || '').trim() || 'Conditions unavailable';

  return (
    <article
      aria-labelledby={headingId}
      className="current-conditions-station-summary d-flex min-w-0 flex-shrink-0 flex-column gap-2"
    >
      <header className="min-w-0">
        <div className="small text-uppercase text-secondary fw-semibold">
          {isNearestStation ? 'Nearest reporting station' : 'Reporting station'}
        </div>
        <h2 id={headingId} className="m-0 fs-6 fw-bold text-break">
          {stationName}
        </h2>
        <div className="small text-secondary">
          {observationTime ? `Observed ${observationTime}` : 'Observation time unavailable'}
        </div>
      </header>

      <div
        aria-label="Current observation summary"
        className="current-conditions-station-overview d-flex min-w-0 align-items-center gap-3 rounded-2 border p-2"
        role="group"
      >
        <span aria-hidden="true" className="current-conditions-station-icon flex-shrink-0">
          <ConditionIcons conditionId={condition.icon} size={48} />
        </span>
        <div className="d-flex min-w-0 flex-column">
          <span className="current-conditions-station-temperature fw-bold">{temperatureLabel}</span>
          <span className="text-break">{skyCondition}</span>
        </div>
      </div>

      <WeatherWidgetDetails
        selectedItem={condition}
        details={currentConditionsDetails}
        layoutGroups={currentConditionsDetailGroups}
        containerClassName="weather-map-detail-grid current-conditions-station-detail-grid"
        iconSize={16}
        ariaLabel={`Current conditions at ${stationName}`}
      />
    </article>
  );
};

export default CurrentConditionsStationSummary;
